import React, { useState, useRef } from 'react';
import { FiUpload, FiX, FiImage } from 'react-icons/fi';
import { Button, AlertModal } from './common';

const BookingImageUpload = ({ bookingId, imageType, currentImages = [], currentUserId, currentUserName, onUploadComplete, onDelete, maxImages = 15 }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [descriptions, setDescriptions] = useState({});
  const [alert, setAlert] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const fileInputRef = useRef(null);

  const remainingSlots = maxImages - currentImages.length;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;

    // Check if adding these files would exceed limit
    if (currentImages.length + files.length > maxImages) {
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'Image Limit Reached',
        message: `You can only upload ${remainingSlots} more image(s). Please select fewer images.`
      });
      e.target.value = '';
      return;
    }

    // Validate files
    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        invalidFiles.push(file.name);
      } else if (file.size > 5 * 1024 * 1024) {
        invalidFiles.push(`${file.name} (too large)`);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'Invalid Files',
        message: `Some files were skipped: ${invalidFiles.join(', ')}. Please upload image files under 5MB.`
      });
    }

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    // Create previews
    const newPreviews = [];
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push({
          file,
          preview: reader.result,
          id: Date.now() + Math.random()
        });
        if (newPreviews.length === validFiles.length) {
          setPreviews(prev => [...prev, ...newPreviews]);
          setSelectedFiles(prev => [...prev, ...validFiles]);
        }
      };
      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const removePreview = (previewId) => {
    setPreviews(prev => prev.filter(p => p.id !== previewId));
    setSelectedFiles(prev => {
      const index = prev.findIndex((_, i) => {
        const previewIndex = previews.findIndex(p => p.id === previewId);
        return i === previewIndex;
      });
      if (index !== -1) {
        const newFiles = [...prev];
        newFiles.splice(index, 1);
        return newFiles;
      }
      return prev;
    });
    setDescriptions(prev => {
      const newDesc = { ...prev };
      delete newDesc[previewId];
      return newDesc;
    });
  };

  const handleDescriptionChange = (previewId, description) => {
    setDescriptions(prev => ({
      ...prev,
      [previewId]: description
    }));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'No Images Selected',
        message: 'Please select at least one image to upload.'
      });
      return;
    }

    setUploading(true);

    try {
      const { uploadBookingImage } = await import('../firebase/storage');
      const { addBookingImage } = await import('../firebase/bookings');

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const preview = previews[i];
        const description = descriptions[preview.id] || '';

        // Upload to storage
        const uploadResult = await uploadBookingImage(file, bookingId, imageType);
        
        if (uploadResult.error) {
          setAlert({
            isOpen: true,
            type: 'error',
            title: 'Upload Failed',
            message: `Failed to upload ${file.name}: ${uploadResult.error}`
          });
          setUploading(false);
          return;
        }

        // Create image data
        const imageData = {
          id: `img-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          url: uploadResult.url,
          path: uploadResult.path,
          description: description.trim(),
          type: imageType,
          uploadedAt: new Date().toISOString(),
          uploadedBy: currentUserId,
          uploadedByName: currentUserName || 'Unknown User'
        };

        // Add to booking
        const result = await addBookingImage(bookingId, imageData);
        
        if (result.error) {
          setAlert({
            isOpen: true,
            type: 'error',
            title: 'Save Failed',
            message: `Failed to save image: ${result.error}`
          });
          setUploading(false);
          return;
        }
      }

      // Clear selections
      setSelectedFiles([]);
      setPreviews([]);
      setDescriptions({});
      
      setAlert({
        isOpen: true,
        type: 'success',
        title: 'Upload Successful',
        message: `${selectedFiles.length} image(s) uploaded successfully!`
      });

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Upload Failed',
        message: 'Failed to upload images. Please try again.'
      });
    } finally {
      setUploading(false);
    }
  };

  if (remainingSlots <= 0) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          Maximum {maxImages} images reached for this section.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* File Input */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || remainingSlots <= 0}
          className="flex items-center gap-2"
        >
          <FiUpload size={16} />
          Add Images ({remainingSlots} remaining)
        </Button>
        <p className="text-xs text-gray-500 mt-1">
          You can upload up to {remainingSlots} more image(s). Max 5MB per image.
        </p>
      </div>

      {/* Preview Grid */}
      {previews.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {previews.map((preview) => (
              <div key={preview.id} className="relative">
                <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={preview.preview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => removePreview(preview.id)}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <FiX size={12} />
                  </button>
                </div>
                <textarea
                  placeholder="Add description (optional)"
                  value={descriptions[preview.id] || ''}
                  onChange={(e) => handleDescriptionChange(preview.id, e.target.value)}
                  className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  rows={2}
                  maxLength={200}
                />
              </div>
            ))}
          </div>
          <Button
            onClick={handleUpload}
            isLoading={uploading}
            disabled={uploading || previews.length === 0}
            className="flex items-center gap-2"
          >
            <FiUpload size={16} />
            Upload {previews.length} Image(s)
          </Button>
        </div>
      )}

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />
    </div>
  );
};

export default BookingImageUpload;
